import {
  MessageCircle,
  Github,
  Twitter,
  Users,
  Heart,
  Code2,
  BookOpen,
  ExternalLink,
  Star,
  GitPullRequest,
  Bug,
  Lightbulb,
  Calendar,
  Trophy,
  Sparkles
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import { CommunityBackground } from '@/components/ui/CommunityBackground';

const communityLinksConfig = [
  { key: 'discord', icon: MessageCircle, href: 'https://discord.gg/ochtopus', color: 'bg-[#5865F2]' },
  { key: 'github', icon: Github, href: 'https://github.com/ochtopus/ochtopus', color: 'bg-[#333]' },
  { key: 'twitter', icon: Twitter, href: 'https://twitter.com/ochtopus_ide', color: 'bg-[#1DA1F2]' },
];

const contributionWaysConfig = [
  { key: 'reportBugs', icon: Bug, href: 'https://github.com/ochtopus/ochtopus/issues/new?template=bug_report.md' },
  { key: 'featureRequests', icon: Lightbulb, href: 'https://github.com/ochtopus/ochtopus/issues/new?template=feature_request.md' },
  { key: 'pullRequests', icon: GitPullRequest, href: 'https://github.com/ochtopus/ochtopus/pulls' },
  { key: 'documentation', icon: BookOpen, href: 'https://github.com/ochtopus/ochtopus-docs' },
];

const topContributors = [
  { name: 'Berkant K.', avatar: 'BK', contributions: 245 },
  { name: 'Gökhun Y.', avatar: 'GY', contributions: 128 },
  { name: 'Erkan E.', avatar: 'EE', contributions: 97 },
  { name: 'Ahmet S.', avatar: 'AS', contributions: 82 },
  { name: 'Mehmet D.', avatar: 'MD', contributions: 71 },
];

export function CommunityPage() {
  const { t } = useTranslation();

  return (
    <>
      <SEO {...SEO_CONFIG.community} />
      <div className="pt-24 relative overflow-hidden">
      <CommunityBackground />
      {/* Hero */}
      <section className="section relative">
        <div className="container-sm text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-muted text-accent text-sm font-medium mb-6">
            <Users size={16} />
            {t('community.hero.badge')}
          </div>
          <h1 className="heading-xl mb-4">
            {t('community.hero.title')}{' '}
            <span className="text-gradient">{t('community.hero.titleHighlight')}</span>
          </h1>
          <p className="text-text-secondary text-lg max-w-2xl mx-auto mb-8">
            {t('community.hero.subtitle')}
          </p>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-6 max-w-md mx-auto mb-12">
            <div>
              <div className="text-3xl font-bold text-accent">5K+</div>
              <div className="text-text-tertiary text-sm">{t('community.stats.githubStars')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-accent">2.5K+</div>
              <div className="text-text-tertiary text-sm">{t('community.stats.discordMembers')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-accent">100+</div>
              <div className="text-text-tertiary text-sm">{t('community.stats.contributors')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Platforms */}
      <section className="section bg-bg-surface">
        <div className="container">
          <h2 className="heading-lg text-center mb-12">{t('community.platforms.title')}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {communityLinksConfig.map((link) => (
              <a
                key={link.key}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="card hover:border-accent transition-all duration-300 group"
              >
                <div className={`w-12 h-12 rounded-xl ${link.color} flex items-center justify-center mb-4`}>
                  <link.icon className="text-white" size={24} />
                </div>
                <h3 className="heading-sm mb-2 group-hover:text-accent transition-colors flex items-center gap-2">
                  {t(`community.platforms.${link.key}.title`)}
                  <ExternalLink size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </h3>
                <p className="text-text-secondary text-sm mb-4">{t(`community.platforms.${link.key}.description`)}</p>
                <div className="text-accent text-sm font-medium">{t(`community.platforms.${link.key}.stats`)}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contribute */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">{t('community.contribute.title')}</h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('community.contribute.subtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contributionWaysConfig.map((way) => (
              <a
                key={way.key}
                href={way.href}
                target="_blank"
                rel="noopener noreferrer"
                className="card text-center hover:border-accent transition-colors group"
              >
                <div className="w-12 h-12 rounded-full bg-accent-muted flex items-center justify-center mx-auto mb-4">
                  <way.icon className="text-accent" size={24} />
                </div>
                <h3 className="font-semibold mb-2 group-hover:text-accent transition-colors">{t(`community.contribute.ways.${way.key}.title`)}</h3>
                <p className="text-text-secondary text-sm">{t(`community.contribute.ways.${way.key}.description`)}</p>
              </a>
            ))}
          </div>

          {/* Getting Started Guide */}
          <div className="mt-12 card bg-accent-glow border-accent/20">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-full bg-accent flex items-center justify-center">
                  <Code2 className="text-white" size={32} />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="heading-sm mb-2">{t('community.contribute.firstTime.title')}</h3>
                <p className="text-text-secondary mb-4">
                  {t('community.contribute.firstTime.description')}
                </p>
                <a
                  href="https://github.com/ochtopus/ochtopus/blob/main/CONTRIBUTING.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary inline-flex"
                >
                  {t('community.contribute.firstTime.cta')}
                  <ExternalLink size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Top Contributors */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4 flex items-center justify-center gap-3">
              <Trophy className="text-accent" />
              {t('community.contributors.title')}
            </h2>
            <p className="text-text-secondary">
              {t('community.contributors.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 max-w-3xl mx-auto">
            {topContributors.map((contributor, index) => (
              <a
                key={contributor.name}
                href={`https://github.com/${contributor.name}`}
                target="_blank"
                rel="noopener noreferrer"
                className="card text-center hover:border-accent transition-colors"
              >
                {index === 0 && (
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
                    <Star size={14} className="text-white" />
                  </div>
                )}
                <div className="w-12 h-12 rounded-full bg-accent-muted flex items-center justify-center mx-auto mb-3 text-accent font-bold text-lg">
                  {contributor.avatar}
                </div>
                <div className="font-medium text-sm truncate">{contributor.name}</div>
                <div className="text-text-tertiary text-xs">{contributor.contributions} {t('community.contributors.commits')}</div>
              </a>
            ))}
          </div>

          <div className="text-center mt-8">
            <a
              href="https://github.com/ochtopus/ochtopus/graphs/contributors"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:underline inline-flex items-center gap-2"
            >
              {t('community.contributors.viewAll')}
              <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* Events */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4 flex items-center justify-center gap-3">
              <Calendar className="text-accent" />
              {t('community.events.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {[0, 1].map((index) => (
              <div key={index} className="card">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-accent-muted flex items-center justify-center flex-shrink-0">
                    <Calendar className="text-accent" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t(`community.events.${index}.title`)}</h3>
                    <div className="text-accent text-sm mb-2">{t(`community.events.${index}.date`)} - {t(`community.events.${index}.time`)}</div>
                    <p className="text-text-secondary text-sm">{t(`community.events.${index}.description`)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="section bg-bg-surface">
        <div className="container-sm">
          <div className="card p-8 text-center">
            <Sparkles className="text-accent mx-auto mb-4" size={32} />
            <h2 className="heading-md mb-4">{t('community.newsletter.title')}</h2>
            <p className="text-text-secondary mb-6 max-w-md mx-auto">
              {t('community.newsletter.subtitle')}
            </p>
            <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder={t('community.newsletter.placeholder')}
                className="flex-1 px-4 py-3 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none text-text-primary"
              />
              <button type="submit" className="btn btn-primary">
                {t('community.newsletter.subscribe')}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container-sm text-center">
          <Heart className="text-accent mx-auto mb-6" size={48} />
          <h2 className="heading-lg mb-4">{t('community.cta.title')}</h2>
          <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
            {t('community.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://discord.gg/ochtopus"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              <MessageCircle size={18} />
              {t('community.cta.joinDiscord')}
            </a>
            <a
              href="https://github.com/ochtopus/ochtopus"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary"
            >
              <Github size={18} />
              {t('community.cta.starGithub')}
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
